import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
} from '@mui/material';
import {
  Palette,
  Notifications,
  Language,
  Security,
  Assessment,
  School,
  WorkspacePremium,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { TeacherContext } from '../../context/TeacherContext';
import apiAxiosInstance from '../../utils/axiosInstance';
import { useFamily } from '../../context/FamilyContext';
import DeleteLearnerDialog from '../Homeschool/DeleteLearnerDialog';

interface SubscriptionInfo {
  status: string;
  plan: string;
  nextBillingDate?: string;
  amount: string;
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { teacher, setTeacher } = useContext(TeacherContext);
  const isParent = teacher?.role === 'parent';
  const { family, learners, restoreLearner } = useFamily();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [subjectDialogOpen, setSubjectDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [savingSubject, setSavingSubject] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Subject configuration state
  const [subjectConfig, setSubjectConfig] = useState({
    primarySubject: teacher?.primarySubject || 'ELA',
    focusSubject: teacher?.subjectPreferences?.focusSubject || 'Reading',
    analysisMode: teacher?.subjectPreferences?.analysisMode || 'reading-comprehension',
    standardsSystem: teacher?.subjectPreferences?.standardsSystem || 'TEKS'
  });

  const handleCancelSubscription = async () => {
    setLoading(true);
    try {
      const response = await fetch('http://localhost:5002/subscription/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Update subscription info after cancellation
        setSubscriptionInfo(null);
      } else {
        console.error('Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setCancelDialogOpen(false);
    }
  };

  const handleSaveSubjectConfiguration = async () => {
    setSavingSubject(true);
    try {
      const updateData = {
        primarySubject: subjectConfig.primarySubject,
        subjectPreferences: {
          focusSubject: subjectConfig.focusSubject,
          analysisMode: subjectConfig.analysisMode,
          standardsSystem: subjectConfig.standardsSystem
        }
      };

              const response = await apiAxiosInstance.put(`/auth/profile/${teacher?._id}`, updateData);
      
      if (response.status === 200) {
        // Update teacher context
        setTeacher(prev => prev ? {
          ...prev,
          ...updateData
        } : prev);
        
        setSuccessMessage('Subject configuration saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        setSubjectDialogOpen(false);
      }
    } catch (error) {
      console.error('Error saving subject configuration:', error);
    } finally {
      setSavingSubject(false);
    }
  };

  const [restoringId, setRestoringId] = useState<string | null>(null);
  const handleRestoreLearner = async (learnerId: string) => {
    setRestoringId(learnerId);
    try {
      await restoreLearner(learnerId);
    } catch (err) {
      console.error('Error restoring learner:', err);
    } finally {
      setRestoringId(null);
    }
  };

  const getSubjectDescription = () => {
    const config = teacher?.subjectPreferences || subjectConfig;
    const currentSubject = teacher?.currentSubjectContext?.activeSubject || teacher?.primarySubject || 'ELA';
    const multiSubjectIndicator = teacher?.teachingSubjects?.length && teacher.teachingSubjects.length > 1 ? ' (Multi-Subject Teacher)' : '';
    return `Active Subject: ${currentSubject} | Focus: ${config.focusSubject} | Analysis: ${config.analysisMode}${multiSubjectIndicator}`;
  };

  const settingsGroups = [
    {
      title: 'Subscription',
      icon: <WorkspacePremium />,
      description: subscriptionInfo ? 
        `Current plan: ${subscriptionInfo.plan} (${subscriptionInfo.amount}/month)` :
        'Manage your subscription plan and billing details.',
      action: () => navigate('/subscription'),
      secondaryAction: subscriptionInfo ? {
        label: 'Cancel Subscription',
        onClick: () => setCancelDialogOpen(true),
        color: 'error' as const
      } : undefined
    },
    {
      title: 'Appearance',
      icon: <Palette />,
      description: 'Customize the look and feel of your dashboard, including themes and layout preferences.'
    },
    {
      title: 'Notifications',
      icon: <Notifications />,
      description: 'Configure email notifications for student progress, upcoming sessions, and reminders.'
    },
    {
      title: 'Language & Region',
      icon: <Language />,
      description: 'Set your preferred language and regional settings for dates and measurements.'
    },
    {
      title: 'Security',
      icon: <Security />,
      description: 'Manage your password, enable two-factor authentication, and review login history.'
    },
    {
      title: 'Reports & Analytics',
      icon: <Assessment />,
      description: 'Configure default settings for student progress reports and data analytics.'
    },
    {
      title: 'Subject & Teaching Preferences',
      icon: <School />,
      description: getSubjectDescription(),
      action: () => setSubjectDialogOpen(true),
      isConfigurable: true
    },
  ];

  return (
    <Box sx={{ p: 4, maxWidth: 800, margin: '0 auto', mt: 10 }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Configure your TeachAssist experience with these settings.
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Phase 7 (hardening) - parent-only: deletion is a genuinely
          irreversible-in-spirit action, so it lives in its own section
          rather than folded into the generic settingsGroups list below. */}
      {isParent && (
        <Paper sx={{ mb: 3, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Your children
          </Typography>
          {learners.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No learners on this account yet.
            </Typography>
          ) : (
            <List disablePadding>
              {learners.map((learner) => (
                <ListItem key={learner._id} sx={{ px: 0 }}>
                  <ListItemText primary={`${learner.firstName} ${learner.lastName}`} />
                  <DeleteLearnerDialog learnerId={learner._id} learnerFirstName={learner.firstName} />
                </ListItem>
              ))}
            </List>
          )}

          {!!family?.deletedLearners?.length && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Recently removed
              </Typography>
              <List disablePadding>
                {family.deletedLearners.map((learner) => (
                  <ListItem key={learner._id} sx={{ px: 0 }}>
                    <ListItemText
                      primary={`${learner.firstName} ${learner.lastName}`}
                      secondary="Permanently deleted 30 days after removal, unless restored."
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleRestoreLearner(learner._id)}
                      disabled={restoringId === learner._id}
                    >
                      {restoringId === learner._id ? <CircularProgress size={20} /> : 'Restore'}
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      )}

      <List>
        {settingsGroups.map((group, index) => (
          <React.Fragment key={group.title}>
            <Paper sx={{ mb: 3 }}>
              {group.action ? (
                <ListItemButton onClick={group.action} sx={{ py: 3 }}>
                  <ListItemIcon>
                    {group.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={group.title}
                    secondary={group.description}
                    primaryTypographyProps={{
                      variant: 'h6',
                      gutterBottom: true
                    }}
                    secondaryTypographyProps={{
                      variant: 'body2'
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {group.isConfigurable && (
                      <Chip 
                        label="Configurable" 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                      />
                    )}
                    {group.secondaryAction && (
                      <Button
                        variant="outlined"
                        color={group.secondaryAction.color}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          group.secondaryAction?.onClick();
                        }}
                      >
                        {group.secondaryAction.label}
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                    >
                      {group.isConfigurable ? 'Configure' : 'Manage'}
                    </Button>
                  </Box>
                </ListItemButton>
              ) : (
                <ListItem sx={{ py: 3 }}>
                  <ListItemIcon>
                    {group.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={group.title}
                    secondary={group.description}
                    primaryTypographyProps={{
                      variant: 'h6',
                      gutterBottom: true
                    }}
                    secondaryTypographyProps={{
                      variant: 'body2'
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                    >
                      Manage
                    </Button>
                  </Box>
                </ListItem>
              )}
            </Paper>
          </React.Fragment>
        ))}
      </List>

      {/* Subject Configuration Dialog */}
      <Dialog
        open={subjectDialogOpen}
        onClose={() => setSubjectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Subject & Teaching Preferences
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Configure your primary subject area and analysis preferences. This helps optimize AI analysis for Google Forms imports and other assessments.
          </DialogContentText>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Primary Subject</InputLabel>
              <Select
                value={subjectConfig.primarySubject}
                label="Primary Subject"
                onChange={(e) => setSubjectConfig(prev => ({ ...prev, primarySubject: e.target.value as 'ELA' | 'Math' | 'Science' | 'Social Studies' | 'Reading' }))}
              >
                <MenuItem value="ELA">English Language Arts (ELA)</MenuItem>
                <MenuItem value="Math">Mathematics</MenuItem>
                <MenuItem value="Science">Science</MenuItem>
                <MenuItem value="Social Studies">Social Studies</MenuItem>
                <MenuItem value="Reading">Reading</MenuItem>
              </Select>
            </FormControl>

            {subjectConfig.primarySubject === 'ELA' && (
              <FormControl fullWidth>
                <InputLabel>Focus Area</InputLabel>
                <Select
                  value={subjectConfig.focusSubject}
                  label="Focus Area"
                  onChange={(e) => setSubjectConfig(prev => ({ ...prev, focusSubject: e.target.value }))}
                >
                  <MenuItem value="Reading">Reading Comprehension</MenuItem>
                  <MenuItem value="Writing">Writing & Grammar</MenuItem>
                  <MenuItem value="Literature">Literature Analysis</MenuItem>
                  <MenuItem value="Language Arts">General Language Arts</MenuItem>
                </Select>
              </FormControl>
            )}

            {(subjectConfig.primarySubject === 'ELA' || subjectConfig.primarySubject === 'Reading') && (
              <FormControl fullWidth>
                <InputLabel>Analysis Mode</InputLabel>
                <Select
                  value={subjectConfig.analysisMode}
                  label="Analysis Mode"
                  onChange={(e) => setSubjectConfig(prev => ({ ...prev, analysisMode: e.target.value as 'reading-comprehension' | 'literary-analysis' | 'informational-text' | 'general' }))}
                >
                  <MenuItem value="reading-comprehension">Reading Comprehension</MenuItem>
                  <MenuItem value="literary-analysis">Literary Analysis</MenuItem>
                  <MenuItem value="informational-text">Informational Text</MenuItem>
                  <MenuItem value="general">General Analysis</MenuItem>
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth>
              <InputLabel>Standards System</InputLabel>
              <Select
                value={subjectConfig.standardsSystem}
                label="Standards System"
                onChange={(e) => setSubjectConfig(prev => ({ ...prev, standardsSystem: e.target.value as 'CCSS' | 'TEKS' | 'State-Specific' }))}
              >
                <MenuItem value="TEKS">TEKS (Texas)</MenuItem>
                <MenuItem value="CCSS">Common Core State Standards</MenuItem>
                <MenuItem value="State-Specific">State-Specific Standards</MenuItem>
              </Select>
            </FormControl>

            {/* Future Multi-Subject Preview */}
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: 'primary.main', 
              color: 'primary.contrastText', 
              borderRadius: 1,
              border: '2px dashed',
              borderColor: 'primary.light'
            }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                🚀 Coming Soon: Multi-Subject Support
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                For teachers who teach multiple subjects, you'll be able to:
              </Typography>
              <Typography variant="caption" component="div" sx={{ ml: 1 }}>
                • Add multiple teaching subjects (Math, Science, etc.)<br/>
                • Switch active subject for AI analysis<br/>
                • Set different preferences per subject<br/>
                • Quick-switch during Google Forms import
              </Typography>
              <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                Currently optimized for Reading/ELA. Other subjects coming in future updates!
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubjectDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveSubjectConfiguration}
            variant="contained"
            disabled={savingSubject}
          >
            {savingSubject ? <CircularProgress size={20} /> : 'Save Configuration'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancellation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
      >
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>
            Keep Subscription
          </Button>
          <Button 
            onClick={handleCancelSubscription} 
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings; 